/**
 * Thẻ "Cơ cấu" — CHỈ XEM (CEO 7b + 17a, 28/10/2026): Miền → Đội → Tiểu đội,
 * người và chức đúng như HRM gửi. Sửa cơ cấu làm ở HRM, nên ở đây không có nút
 * thêm người / gắn chức.
 *
 * Kiểu thẻ đội (CEO 1a · 2a · 3a · 4a · 5a, 31/10/2026): mỗi đội một thẻ có vạch
 * màu cố định, tiểu đội xếp cột cạnh nhau, người thụt vào dưới tên tiểu đội;
 * CSKH chung cả đội gom lên đầu thẻ; "(từ …)" chỉ hiện ở người lệch ngày chung.
 */
import { useMemo, useState, type ReactNode } from "react";
import { Badge } from "@/ui/badge";
import { EmptyState } from "@/components/EmptyState";
import type { SalesMember, SalesOrgView, SalesRole, SalesUnit } from "../api";
import { MEMBER_KIND_LABEL, ROLE_LABEL, UNIT_KIND_LABEL, dayText } from "../labels";
import { activeOn, commonStart, sharedCskh, teamColors } from "../structureLayout";
import { PersonMark, SquadMark } from "./marks";

function spanText(start: string, end: string): string {
  return end ? `${dayText(start)} – ${dayText(end)}` : `từ ${dayText(start)}`;
}

/** "(từ …)" chỉ khi lệch ngày chung hoặc đã kết thúc; ngày vẫn xem được khi đưa chuột vào tên. */
function SpanHint({ start, end, common }: { start: string; end: string; common: string }) {
  if (!end && start === common) return null;
  return <span className="text-xs text-muted">({spanText(start, end)})</span>;
}

function PersonName({ name, unresolved, staffId }: { name: string; unresolved: boolean; staffId: string }) {
  return (
    <span className="inline-flex min-w-0 flex-wrap items-center gap-1">
      <PersonMark staffId={staffId} name={name || staffId} size={22} />
      {unresolved ? (
        <Badge variant="destructive" title="Có trong cơ cấu HRM nhưng GNPF chưa có hồ sơ người này">
          Chưa có hồ sơ GNPF
        </Badge>
      ) : null}
    </span>
  );
}

function RoleLine({ role, today, common }: { role: SalesRole; today: string; common: string }) {
  const ended = !activeOn(role.startDate, role.endDate, today);
  return (
    <li
      className={ended ? "flex flex-wrap items-center gap-x-2 gap-y-1 text-muted" : "flex flex-wrap items-center gap-x-2 gap-y-1"}
      title={spanText(role.startDate, role.endDate)}
    >
      <span className="text-muted">{ROLE_LABEL[role.role] ?? role.role}</span>
      <PersonName name={role.staffName} unresolved={role.unresolved} staffId={role.staffId} />
      <SpanHint start={role.startDate} end={role.endDate} common={common} />
    </li>
  );
}

function MemberLine({ m, today, common, showKind }: { m: SalesMember; today: string; common: string; showKind: boolean }) {
  const ended = !activeOn(m.startDate, m.endDate, today);
  return (
    <li
      className={ended ? "flex flex-wrap items-center gap-x-2 gap-y-1 text-muted" : "flex flex-wrap items-center gap-x-2 gap-y-1"}
      title={spanText(m.startDate, m.endDate)}
    >
      <PersonName name={m.staffName} unresolved={m.unresolved} staffId={m.staffId} />
      {showKind ? <Badge variant="outline">{MEMBER_KIND_LABEL[m.kind] ?? m.kind}</Badge> : null}
      <SpanHint start={m.startDate} end={m.endDate} common={common} />
    </li>
  );
}

function countText(parts: [number, string][]): string {
  return parts
    .filter(([n]) => n > 0)
    .map(([n, label]) => `${n} ${label}`)
    .join(" · ");
}

function distinctStaff(members: SalesMember[], kind: string): number {
  return new Set(members.filter((m) => m.kind === kind).map((m) => m.staffId)).size;
}

type Props = { org: SalesOrgView };

export function StructureTab({ org }: Props) {
  const [showEnded, setShowEnded] = useState(false);
  const today = org.today;

  const children = useMemo(() => {
    const out = new Map<string, SalesUnit[]>();
    for (const u of org.units) {
      const list = out.get(u.parentId) ?? [];
      list.push(u);
      out.set(u.parentId, list);
    }
    for (const list of out.values()) list.sort((a, b) => a.name.localeCompare(b.name, "vi"));
    return out;
  }, [org.units]);

  const colors = useMemo(() => teamColors(org.units.filter((u) => u.kind === "team")), [org.units]);

  const visible = (start: string, end: string) => showEnded || activeOn(start, end, today);
  const visibleKids = (id: string) => (children.get(id) ?? []).filter((u) => visible(u.startDate, u.endDate));
  const rolesOf = (unitId: string) => org.roles.filter((r) => r.unitId === unitId && visible(r.startDate, r.endDate));
  const membersOf = (squadId: string) =>
    org.members
      .filter((m) => m.squadId === squadId && visible(m.startDate, m.endDate))
      .sort((a, b) => b.kind.localeCompare(a.kind) || a.staffName.localeCompare(b.staffName, "vi"));

  /** Mọi tiểu đội dưới một đơn vị (kể cả lồng sâu). */
  const squadsUnder = (u: SalesUnit): SalesUnit[] =>
    u.kind === "squad" ? [u] : visibleKids(u.id).flatMap((c) => squadsUnder(c));

  const head = org.roles.filter((r) => r.role === "head" && visible(r.startDate, r.endDate));

  const unitHeader = (u: SalesUnit, size: number, extra: ReactNode, counts: string) => {
    const ended = !activeOn(u.startDate, u.endDate, today);
    return (
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-xs uppercase tracking-wide text-muted">{UNIT_KIND_LABEL[u.kind] ?? u.kind}</span>
        <SquadMark squadId={u.id} name={u.name} size={size} className="font-semibold">
          {extra}
        </SquadMark>
        {counts ? <span className="text-xs text-muted">{counts}</span> : null}
        {ended ? <Badge variant="outline">Đã giải thể {dayText(u.endDate)}</Badge> : null}
      </div>
    );
  };

  const renderSquad = (s: SalesUnit, hidden: Set<string>, common: string) => {
    const roles = rolesOf(s.id);
    const members = membersOf(s.id).filter((m) => !hidden.has(m.id));
    const sales = distinctStaff(members, "sales");
    const cskh = distinctStaff(members, "cskh");
    return (
      <li key={s.id} className="min-w-0 rounded-md border border-border bg-muted/30 p-3" data-testid={`squad-${s.id}`}>
        {unitHeader(s, 28, null, countText([[sales, "Sales"], [cskh, "CSKH"]]))}
        {/* Người thụt vào dưới tên tiểu đội (CEO 31/10) — logo 28 + khoảng 8 = 36px. */}
        <div className="mt-2 space-y-1 pl-4 text-sm md:pl-9">
          {roles.length > 0 ? (
            <ul className="space-y-1">
              {roles.map((r) => (
                <RoleLine key={r.id} role={r} today={today} common={common} />
              ))}
            </ul>
          ) : null}
          {members.length > 0 ? (
            <ul className="space-y-1">
              {members.map((m) => (
                <MemberLine key={m.id} m={m} today={today} common={common} showKind={m.kind !== "sales"} />
              ))}
            </ul>
          ) : (
            <p className="text-muted">Chưa có người</p>
          )}
        </div>
      </li>
    );
  };

  const squadGrid = (squads: SalesUnit[], hidden: Set<string>, common: string) =>
    squads.length > 0 ? (
      <ul className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">{squads.map((s) => renderSquad(s, hidden, common))}</ul>
    ) : null;

  const renderTeam = (t: SalesUnit) => {
    const squads = visibleKids(t.id).filter((u) => u.kind === "squad");
    const roles = rolesOf(t.id);
    const allMembers = squadsUnder(t).flatMap((s) => membersOf(s.id));
    const { shared, sharedIds } = sharedCskh(
      squads.map((s) => s.id),
      allMembers,
      today,
    );
    const common = commonStart([...allMembers, ...roles].map((x) => x.startDate));
    const hasLead = roles.some((r) => r.role === "team_lead" && activeOn(r.startDate, r.endDate, today));
    const counts = countText([
      [squads.length, "tiểu đội"],
      [distinctStaff(allMembers, "sales"), "Sales"],
      [distinctStaff(allMembers, "cskh"), "CSKH"],
    ]);
    return (
      <li
        key={t.id}
        className="rounded-r-lg border border-l-4 border-border bg-surface p-3"
        style={{ borderLeftColor: colors.get(t.id) }}
        data-testid={`team-${t.id}`}
      >
        {unitHeader(t, 32, `Đội ${t.name}`, counts)}
        <ul className="mt-2 space-y-1 text-sm">
          {roles.map((r) => (
            <RoleLine key={r.id} role={r} today={today} common={common} />
          ))}
          {!hasLead ? (
            <li className="flex items-center gap-2">
              <span className="text-muted">{ROLE_LABEL.team_lead}</span>
              <span className="text-warning-text">chưa có</span>
            </li>
          ) : null}
          {shared.length > 0 ? (
            <li className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="text-muted">CSKH chung</span>
              {shared.map((m) => (
                <span key={m.id} className="inline-flex items-center gap-1" title={spanText(m.startDate, m.endDate)}>
                  <PersonName name={m.staffName} unresolved={m.unresolved} staffId={m.staffId} />
                  <SpanHint start={m.startDate} end={m.endDate} common={common} />
                </span>
              ))}
            </li>
          ) : null}
        </ul>
        {common ? <p className="mt-1 text-xs text-muted">Cơ cấu đội từ {dayText(common)}</p> : null}
        <div className="mt-3">{squadGrid(squads, sharedIds, common)}</div>
      </li>
    );
  };

  const renderRegion = (r: SalesUnit) => {
    const kids = visibleKids(r.id);
    const teams = kids.filter((u) => u.kind === "team");
    const looseSquads = kids.filter((u) => u.kind === "squad");
    const allMembers = squadsUnder(r).flatMap((s) => membersOf(s.id));
    const roles = rolesOf(r.id);
    const common = commonStart([...allMembers, ...roles].map((x) => x.startDate));
    const counts = countText([
      [teams.length, "đội"],
      [squadsUnder(r).length, "tiểu đội"],
      [distinctStaff(allMembers, "sales"), "Sales"],
      [distinctStaff(allMembers, "cskh"), "CSKH"],
    ]);
    return (
      <section key={r.id} className="space-y-2" data-testid={`region-${r.id}`}>
        {unitHeader(r, 28, null, counts)}
        {roles.length > 0 ? (
          <ul className="space-y-1 text-sm">
            {roles.map((x) => (
              <RoleLine key={x.id} role={x} today={today} common={common} />
            ))}
          </ul>
        ) : null}
        {teams.length > 0 ? <ul className="space-y-3">{teams.map(renderTeam)}</ul> : null}
        {squadGrid(looseSquads, new Set(), common)}
      </section>
    );
  };

  const roots = visibleKids("");

  return (
    <div className="space-y-3">
      {org.rejected ? (
        <div role="alert" className="rounded-md border border-destructive bg-surface p-3 text-sm text-destructive">
          <p className="font-medium">HRM gửi cơ cấu bản số {org.rejected.revision} nhưng GNPF chưa nhận được:</p>
          <p className="mt-1">{org.rejected.reason}</p>
          <p className="mt-1 text-xs">
            Màn vẫn hiện bản số {org.revision}. Báo phòng Nhân sự sửa trên HRM rồi gửi lại.
          </p>
        </div>
      ) : null}
      {org.revision === 0 ? (
        <EmptyState
          title="HRM chưa gửi cơ cấu Phòng Kinh doanh"
          description="Cơ cấu miền, đội, tiểu đội và người trong từng tiểu đội được sắp trên HRM. Khi HRM gửi sang, màn này tự hiện."
        />
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted">
            <span>
              Bản số {org.revision} do HRM gửi · cập nhật lúc{" "}
              {org.receivedAt
                ? new Date(org.receivedAt).toLocaleString("vi-VN", { timeZone: "Asia/Ho_Chi_Minh" })
                : "—"}
            </span>
            <label className="flex min-h-11 items-center gap-2">
              <input type="checkbox" checked={showEnded} onChange={(e) => setShowEnded(e.target.checked)} />
              Hiện cả người và đơn vị đã kết thúc
            </label>
          </div>
          {head.length > 0 ? (
            <ul className="space-y-1 rounded-lg border border-border bg-surface p-3 text-sm">
              {head.map((r) => (
                <RoleLine key={r.id} role={r} today={today} common="" />
              ))}
            </ul>
          ) : null}
          <div className="space-y-6">
            {roots.map((u) =>
              u.kind === "region" ? renderRegion(u) : u.kind === "team" ? <ul key={u.id}>{renderTeam(u)}</ul> : null,
            )}
            {squadGrid(
              roots.filter((u) => u.kind === "squad"),
              new Set(),
              "",
            )}
          </div>
          <p className="text-xs text-muted">
            Muốn thêm người, chuyển tiểu đội hay gắn chức thì làm trên HRM — GNPF chỉ nhận về để tính lương.
          </p>
        </>
      )}
    </div>
  );
}
